/**
 * Extracts the label from a Solr facet value, dropping the `|id` suffix.
 * Use for non-person facets — legal areas contain commas ("Klima-, miljø- og
 * energirett") that `extractName` would misread as "Last, First".
 */
export function extractLabel(value: string): string {
  return value.includes('|') ? value.split('|')[0] ?? value : value
}

/**
 *  Extracts the name from a Solr facet value and normalizes format
 *  Transforms "Last, First" to "First Last" for display
 */
export function extractName(value: string): string {
  const name = extractLabel(value)

  // Transform "Last, First" to "First Last"
  if (name.includes(',')) {
    const parts = name.split(',').map(part => part.trim())
    return parts.length === 2 ? `${parts[1]} ${parts[0]}` : name
  }

  return name
}

/**
 * Extracts the last name from a Norwegian full name
 * Assumes format: "Fornavn Etternavn" or "Fornavn Mellomnavn Etternavn"
 * Returns the last space-separated word as the last name
 */
export function getLastName(fullName: string): string {
  const trimmed = fullName.trim()
  const words = trimmed.split(/\s+/)
  return words[words.length - 1] || trimmed
}

/**
 * Extracts the ID from a Solr facet value
 */
export function extractId(value: string): string {
  return value.includes('|') ? value.split('|')[1] ?? value : value
}

interface FacetEntry {
  value: string
  count: number
  bestCount: number
}
/**
 * Deduplicate facet entries by ID first, then by normalized name.
 * Sums counts and keeps the name variant with the highest individual count.
 */
export function deduplicateFacets(facets: Array<{ value: string, count: number }>): Array<{ value: string, count: number }> {
  const idIndex = new Map<string, FacetEntry>()
  const nameIndex = new Map<string, FacetEntry>()
  const entries: FacetEntry[] = []

  for (const facet of facets) {
    const id = extractId(facet.value)
    const name = normalizeFacetName(extractName(facet.value))

    const existing = idIndex.get(id) ?? nameIndex.get(name)
    if (existing) {
      existing.count += facet.count
      if (facet.count > existing.bestCount) {
        existing.bestCount = facet.count
        existing.value = facet.value
      }
      if (!idIndex.has(id)) {
        idIndex.set(id, existing)
      }
    }
    else {
      const entry: FacetEntry = { value: facet.value, count: facet.count, bestCount: facet.count }
      idIndex.set(id, entry)
      nameIndex.set(name, entry)
      entries.push(entry)
    }
  }

  return entries.map(({ value, count }) => ({ value, count }))
}

export function normalizeFacetName(name: string): string {
  return name.replace(/\s+/g, ' ').replace(/[.,]/g, '').trim().toLowerCase()
}

/**
 * Generic function to process facets and map them to desired format
 */
export function processFacets(
  facets: Array<{ value: string, count: number }>,
  mapper: (item: { value: string, count: number }) => { value: string, label: string },
  options?: { includeZero?: boolean },
): Array<{ value: string, label: string, disabled: boolean }> {
  const includeZero = options?.includeZero ?? false
  const array = includeZero ? facets : facets.filter(item => item.count > 0)
  return array.map(item => ({ ...mapper(item), disabled: item.count === 0 }))
}
/**
 * Transform function to convert Solr's alternating value/count array
 * From: ["value1", 5, "value2", 3, "value3", 0]
 * To: [{value: "value1", count: 5}, {value: "value2", count: 3}, {value: "value3", count: 0}]
 */
export function transformFacetField(arr: (string | number)[]): Array<{ value: string, count: number }> {
  const result: Array<{ value: string, count: number }> = []

  for (let i = 0; i < arr.length; i += 2) {
    const value = arr[i]
    const count = arr[i + 1]

    if (typeof value === 'string' && typeof count === 'number') {
      result.push({ value, count })
    }
  }

  return result
}
